import { useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { showNotificationModal } from '../../../store/slices/notification/notificationModalSlice';

import NotificationIconButton from './NotificationIconButton';
import NotificationHeader from './NotificationHeader';
import NotificationList from './NotificationList';

import { useNotification } from '../../../hooks/useNotification';

const DropdownNotification = () => {
  const dispatch = useDispatch();

  const { notifications, unreadCount, totalCount, refreshOnOpen } =
    useNotification();

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const trigger = useRef<HTMLAnchorElement | null>(null);

  const dropdown = useRef<HTMLDivElement | null>(null);

  // =========================================================
  // 📥 CUANDO ABRE → SINCRONIZAR BACKEND
  // =========================================================

  useEffect(() => {
    if (!dropdownOpen) return;

    refreshOnOpen();
  }, [dropdownOpen]);

  // =========================================================
  // 📩 ABRIR MODAL DETALLE
  // =========================================================

  const handleNotificationClick = (notification: any) => {
    dispatch(showNotificationModal(notification));

    setDropdownOpen(false);
  };

  // =========================================================
  // 🔔 TOGGLE DROPDOWN
  // =========================================================

  const handleToggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  // =========================================================
  // 🔒 CERRAR CLICK FUERA
  // =========================================================

  useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      const target = event.target as Node;

      if (!dropdown.current || !trigger.current) {
        return;
      }

      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      ) {
        return;
      }

      setDropdownOpen(false);
    };

    document.addEventListener('click', clickHandler);

    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [dropdownOpen]);

  // =========================================================
  // ⌨️ ESCAPE
  // =========================================================

  useEffect(() => {
    const keyHandler = ({ key }: KeyboardEvent) => {
      if (!dropdownOpen || key !== 'Escape') {
        return;
      }

      setDropdownOpen(false);
    };

    document.addEventListener('keydown', keyHandler);

    return () => {
      document.removeEventListener('keydown', keyHandler);
    };
  }, [dropdownOpen]);

  return (
    <li className="relative">
      {/* 🔔 BOTÓN */}
      <NotificationIconButton
        triggerRef={trigger}
        unreadCount={unreadCount}
        onClick={handleToggleDropdown}
      />

      {/* 📩 DROPDOWN */}
      {dropdownOpen && (
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
          className="
            absolute -right-27 mt-2.5
            flex h-90 w-75 flex-col
            rounded-lg
            border border-stroke
            bg-white
            shadow-default
            dark:border-strokedark
            dark:bg-boxdark
            sm:right-0 sm:w-80
          "
        >
          {/* 🧾 HEADER */}
          <NotificationHeader
            unreadCount={unreadCount}
            totalCount={totalCount}
          />

          {/* 📋 LISTA */}
          <NotificationList
            notifications={notifications}
            onSelect={handleNotificationClick}
          />
        </div>
      )}
    </li>
  );
};

export default DropdownNotification;
